import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SyncStatus {
  isSyncing: boolean;
  lastTriggeredAt: number | null;
}

const SYNC_DEBOUNCE_MS = 10000; // Don't trigger sync more than once every 10 seconds

export function useBackgroundSync() {
  const queryClient = useQueryClient();
  const syncStatusRef = useRef<Map<string, SyncStatus>>(new Map());

  const triggerBackgroundSync = useCallback(
    async (userId: string, adAccountId: string) => {
      const cacheKey = `${userId}_${adAccountId}`;
      const status = syncStatusRef.current.get(cacheKey);

      // Debounce - don't trigger if we already triggered recently
      if (status?.lastTriggeredAt && Date.now() - status.lastTriggeredAt < SYNC_DEBOUNCE_MS) {
        console.log("Background sync debounced - triggered too recently");
        return;
      }

      // Update status
      syncStatusRef.current.set(cacheKey, {
        isSyncing: true,
        lastTriggeredAt: Date.now(),
      });

      console.log("Triggering background sync for", adAccountId);

      // Fire and forget - don't await, don't block UI
      // Trigger assets sync (FB pages, IG accounts, partnerships)
      supabase.functions
        .invoke("n8n-proxy", {
          body: {
            action: "GET_ASSETS",
            payload: { ad_account_id: adAccountId },
          },
        })
        .then(async ({ data, error }) => {
          if (error) {
            console.error("Assets sync failed (proxy error):", error);
            return;
          }
          console.log("Assets sync completed");
          // Invalidate related queries after sync completes
          // FB pages and IG accounts are now user-scoped (shared across business portfolio)
          queryClient.invalidateQueries({ queryKey: ["cached-facebook-pages", userId] });
          queryClient.invalidateQueries({ queryKey: ["cached-instagram-accounts", userId] });
          // Partnership accounts still require ad_account_id
          queryClient.invalidateQueries({ queryKey: ["cached-partnership-instagram", userId, adAccountId] });
          queryClient.invalidateQueries({ queryKey: ["cached-partnership-facebook-pages", userId, adAccountId] });

          // After FB pages are available, fetch lead gen forms for each page
          try {
            const pages = data?.facebook_pages || data?.pages || [];

            if (pages.length > 0) {
              console.log(`Syncing lead gen forms for ${pages.length} pages...`);

              const formFetchPromises = pages.map((page: { page_id?: string; id?: string }) => {
                const pageId = page.page_id || page.id;
                if (!pageId) return Promise.resolve();

                return supabase.functions.invoke("n8n-proxy", {
                  body: {
                    action: "GET_LEADGEN_FORMS",
                    payload: { page_id: pageId },
                  },
                });
              });

              const results = await Promise.allSettled(formFetchPromises);
              const succeeded = results.filter((r) => r.status === "fulfilled").length;
              const failed = results.filter((r) => r.status === "rejected").length;

              console.log(`Lead gen forms sync: ${succeeded} succeeded, ${failed} failed`);

              // Invalidate lead gen forms cache for all pages
              pages.forEach((page: { page_id?: string; id?: string }) => {
                const pageId = page.page_id || page.id;
                if (pageId) {
                  queryClient.invalidateQueries({ queryKey: ["cached-leadgen-forms", userId, pageId] });
                }
              });
            }
          } catch (parseErr) {
            console.error("Failed to process assets response for lead gen forms sync:", parseErr);
          }
        })
        .catch((err) => console.error("Assets sync failed:", err));

      // Trigger campaigns sync
      supabase.functions
        .invoke("n8n-proxy", {
          body: {
            action: "GET_CAMPAIGNS",
            payload: { ad_account_id: adAccountId },
          },
        })
        .then(({ error }) => {
          if (error) {
            console.error("Campaigns sync failed (proxy error):", error);
            return;
          }
          console.log("Campaigns sync completed");
          queryClient.invalidateQueries({ queryKey: ["cached-campaigns"] });
          queryClient.invalidateQueries({ queryKey: ["cached-ad-sets"] });

          // Ad Sheet V2 uses the standardized ad sets query key
          queryClient.invalidateQueries({ queryKey: ["ad-sets", adAccountId] });
        })
        .catch((err) => console.error("Campaigns sync failed:", err));

      // TODO: Re-enable when catalog_management Advanced Access is approved
      // supabase.functions
      //   .invoke("n8n-proxy", {
      //     body: {
      //       action: "GET_CATALOGS",
      //       payload: { ad_account_id: adAccountId },
      //     },
      //   })
      //   .then(({ error }) => {
      //     if (error) {
      //       console.error("Catalogs sync failed (proxy error):", error);
      //       return;
      //     }
      //     console.log("Catalogs sync completed");
      //     queryClient.invalidateQueries({ queryKey: ["cached-catalogs", userId, adAccountId] });
      //     queryClient.invalidateQueries({ queryKey: ["cached-product-sets", userId, adAccountId] });
      //   })
      //   .catch((err) => console.error("Catalogs sync failed:", err));

      // Trigger pixels sync
      supabase.functions
        .invoke("n8n-proxy", {
          body: {
            action: "GET_PIXELS",
            payload: { ad_account_id: adAccountId },
          },
        })
        .then(({ error }) => {
          if (error) {
            console.error("Pixels sync failed (proxy error):", error);
            return;
          }
          console.log("Pixels sync completed");
          queryClient.invalidateQueries({ queryKey: ["cached-pixels", userId, adAccountId] });
        })
        .catch((err) => console.error("Pixels sync failed:", err));

      // Trigger apps sync
      supabase.functions
        .invoke("n8n-proxy", {
          body: {
            action: "GET_APPS",
            payload: { ad_account_id: adAccountId },
          },
        })
        .then(({ error }) => {
          if (error) {
            console.error("Apps sync failed (proxy error):", error);
            return;
          }
          console.log("Apps sync completed");
          queryClient.invalidateQueries({ queryKey: ["cached-apps", userId, adAccountId] });
        })
        .catch((err) => console.error("Apps sync failed:", err));

      // Update status after triggering all syncs
      syncStatusRef.current.set(cacheKey, {
        isSyncing: false,
        lastTriggeredAt: Date.now(),
      });
    },
    [queryClient],
  );

  const getSyncStatus = useCallback((userId: string, adAccountId: string): SyncStatus => {
    const cacheKey = `${userId}_${adAccountId}`;
    return syncStatusRef.current.get(cacheKey) || { isSyncing: false, lastTriggeredAt: null };
  }, []);

  return {
    triggerBackgroundSync,
    getSyncStatus,
  };
}
